import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { GitGraph, X, Search, Save, Eye, Pencil } from "lucide-react";
import deskGlass, { ACCENT } from "../../styles/deskGlass";
import HomeShell from "../shell/HomeShell";
import MarkdownRenderer from "../common/MarkdownRenderer";
import { BACKGROUNDS } from "../../config/backgrounds";

const glass = deskGlass;

function stripHtml(html) {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, "");
}

function isEditablePath(path) {
  const lower = (path || "").toLowerCase();
  return lower.endsWith(".md") || lower.endsWith(".markdown") || lower.endsWith(".txt");
}

/** Distinguish missing API (Vercel 404) vs missing token vs real GitHub errors. */
function githubClientError(e, fallback) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  const apiMsg = typeof data?.error === "string" ? data.error : null;
  const code = data?.code;

  if (code === "MISSING_TOKEN" || /Missing GITHUB_TOKEN/i.test(apiMsg || "")) {
    return apiMsg;
  }
  // Function missing → HTML/SPA 404 without JSON error body
  if (status === 404 && !apiMsg) {
    return "API /api/github no desplegada (404). Redeploy con api/github.js en Vercel.";
  }
  if (apiMsg) return apiMsg;
  if (status === 401 || status === 403) {
    return "GitHub rechazó el token (401/403). Revisá scopes Contents.";
  }
  return fallback;
}

export default function GithubExplorer() {
  const navigate = useNavigate();

  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [repoError, setRepoError] = useState("");

  const [selected, setSelected] = useState(null); // { owner, repo, defaultBranch }
  const [branch, setBranch] = useState("");

  const [treeEntries, setTreeEntries] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState("");

  const [pathFilter, setPathFilter] = useState("");
  const [mdOnly, setMdOnly] = useState(true);
  const [selectedPath, setSelectedPath] = useState(null);

  const [fileContent, setFileContent] = useState("");
  const [draft, setDraft] = useState("");
  const [fileSha, setFileSha] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState("");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const dirty = editing && draft !== fileContent;
  const canEdit = isEditablePath(selectedPath);

  const filteredFiles = useMemo(() => {
    const q = pathFilter.trim().toLowerCase();
    let filesOnly = treeEntries.filter((e) => e.type === "blob");
    if (mdOnly) {
      filesOnly = filesOnly.filter((e) => isEditablePath(e.path));
    }
    if (!q) return filesOnly.slice(0, 250);
    return filesOnly.filter((e) => e.path.toLowerCase().includes(q)).slice(0, 250);
  }, [treeEntries, pathFilter, mdOnly]);

  const detectType = useCallback((path) => {
    const lower = (path || "").toLowerCase();
    if (lower.endsWith(".md") || lower.endsWith(".markdown")) return "md";
    if (lower.endsWith(".txt")) return "txt";
    if (lower.endsWith(".js") || lower.endsWith(".jsx") || lower.endsWith(".ts") || lower.endsWith(".tsx")) return "code";
    if (lower.endsWith(".json")) return "json";
    return "text";
  }, []);

  const loadRepos = useCallback(async () => {
    setLoadingRepos(true);
    setRepoError("");
    try {
      const { data } = await axios.get("/api/github", { params: { op: "repos" } });
      setRepos(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setRepoError(
        githubClientError(e, "No se pudieron leer tus repos.")
      );
      setRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  const loadTree = useCallback(
    async (nextSelected, nextBranch = null) => {
      if (!nextSelected) return;
      setLoadingTree(true);
      setTreeError("");
      try {
        const { data } = await axios.get("/api/github", {
          params: {
            op: "tree",
            owner: nextSelected.owner,
            repo: nextSelected.repo,
            branch: nextBranch || branch || nextSelected.defaultBranch,
          },
        });
        const tree = Array.isArray(data?.data?.tree) ? data.data.tree : [];
        setTreeEntries(tree);
      } catch (e) {
        setTreeError(githubClientError(e, "No se pudo cargar el árbol del repo."));
        setTreeEntries([]);
      } finally {
        setLoadingTree(false);
      }
    },
    [branch],
  );

  const loadFile = useCallback(
    async (path) => {
      if (!selected || !path) return;
      setLoadingFile(true);
      setFileError("");
      setSaveMsg("");
      setSelectedPath(path);
      setEditing(false);
      try {
        const ref = branch || selected.defaultBranch;
        const { data } = await axios.get("/api/github", {
          params: {
            op: "file",
            owner: selected.owner,
            repo: selected.repo,
            path,
            ref,
          },
        });

        const content = data?.data?.content ?? "";
        setFileContent(content);
        setDraft(content);
        setFileSha(data?.data?.sha || null);
        setFileType(detectType(path));
        if (isEditablePath(path)) setEditing(true);
      } catch (e) {
        setFileError(githubClientError(e, "No se pudo cargar el archivo."));
        setFileContent("");
        setDraft("");
        setFileSha(null);
        setFileType(null);
      } finally {
        setLoadingFile(false);
      }
    },
    [selected, branch, detectType],
  );

  const saveFile = useCallback(async () => {
    if (!selected || !selectedPath || !canEdit) return;
    setSaving(true);
    setFileError("");
    setSaveMsg("");
    try {
      const body = {
        owner: selected.owner,
        repo: selected.repo,
        path: selectedPath,
        content: draft,
        branch: branch || selected.defaultBranch,
        message: `docs: update ${selectedPath}`,
      };
      if (fileSha) body.sha = fileSha;
      const { data } = await axios.put("/api/github?op=file", body);
      const nextSha = data?.data?.sha;
      setFileContent(draft);
      if (nextSha) setFileSha(nextSha);
      setSaveMsg("Guardado en GitHub.");
    } catch (e) {
      setFileError(githubClientError(e, "No se pudo guardar."));
    } finally {
      setSaving(false);
    }
  }, [selected, selectedPath, fileSha, canEdit, draft, branch]);

  useEffect(() => {
    loadRepos();
  }, [loadRepos]);

  useEffect(() => {
    if (!selected) return;
    const nextBranch = selected.defaultBranch || "main";
    setBranch(nextBranch);
    loadTree(selected, nextBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.repo]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        if (editing && dirty && canEdit && !saving) {
          e.preventDefault();
          saveFile();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, dirty, canEdit, saving, saveFile]);

  const repoOptions = useMemo(() => {
    return repos.map((r) => ({
      owner: r.fullName?.split("/")?.[0] || "",
      repo: r.fullName?.split("/")?.[1] || r.name || "",
      defaultBranch: r.defaultBranch || "main",
      label: r.fullName,
    }));
  }, [repos]);

  return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
      <div style={{ position: "relative", padding: 24, minHeight: "calc(100vh - 64px)", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <GitGraph size={22} color={ACCENT} />
            <h1 style={glass.greeting}>GitHub Notes</h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            style={{ ...glass.glassActionBtn, width: 40, height: 40, borderRadius: "50%" }}
            title="Salir"
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
          <div style={{ flex: "0 0 320px", minWidth: 280 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <select
                value={selected ? `${selected.owner}/${selected.repo}` : ""}
                onChange={(e) => {
                  const next = repoOptions.find((o) => `${o.owner}/${o.repo}` === e.target.value);
                  setSelected(next || null);
                  setTreeEntries([]);
                  setFileContent("");
                  setDraft("");
                  setFileSha(null);
                  setFileType(null);
                  setSelectedPath(null);
                  setEditing(false);
                  setSaveMsg("");
                }}
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "10px 12px",
                  outline: "none",
                }}
                disabled={loadingRepos || repoOptions.length === 0}
              >
                {loadingRepos && <option value="">Cargando repos…</option>}
                {!loadingRepos && repoOptions.length === 0 && <option value="">Sin repos</option>}
                {!loadingRepos && repoOptions.length > 0 && !selected && (
                  <option value="">Elegí un repo…</option>
                )}
                {repoOptions.map((o) => (
                  <option key={o.owner + "/" + o.repo} value={`${o.owner}/${o.repo}`} style={{ background: "#0b0b0b" }}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <input
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="branch (main)"
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "10px 12px",
                  outline: "none",
                  fontFamily: "serif",
                }}
                disabled={!selected}
              />
              <button
                type="button"
                onClick={() => loadTree(selected, branch)}
                disabled={!selected || loadingTree}
                style={{
                  ...glass.glassActionBtn,
                  opacity: !selected || loadingTree ? 0.5 : 1,
                  padding: "10px 12px",
                }}
                title="Actualizar árbol"
              >
                {loadingTree ? "…" : "↻"}
              </button>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", background: "rgba(0,0,0,0.25)", borderRadius: 10, padding: "8px 10px" }}>
                <Search size={14} color="#D4AF37" style={{ opacity: 0.55 }} />
                <input
                  value={pathFilter}
                  onChange={(e) => setPathFilter(e.target.value)}
                  placeholder={mdOnly ? "Filtrar .md / .txt…" : "Filtrar archivos…"}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#D4AF37",
                    fontSize: "0.8rem",
                    outline: "none",
                    width: "100%",
                    fontFamily: "serif",
                  }}
                />
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                  color: "rgba(255,255,255,0.65)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                }}
              >
                <input type="checkbox" checked={mdOnly} onChange={(e) => setMdOnly(e.target.checked)} />
                Solo notas (.md / .txt)
              </label>
            </div>

            {repoError && <div style={{ color: "#ef4444", marginBottom: 10 }}>{repoError}</div>}
            {treeError && <div style={{ color: "#ef4444", marginBottom: 10 }}>{treeError}</div>}

            <div style={{ maxHeight: 520, overflow: "auto" }}>
              {filteredFiles.map((e) => (
                <div
                  key={e.path}
                  onClick={() => loadFile(e.path)}
                  style={{
                    cursor: "pointer",
                    padding: "8px 10px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    color: e.path === selectedPath ? "#D4AF37" : "rgba(255,255,255,0.75)",
                    fontSize: "0.78rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: e.path === selectedPath ? "rgba(212,175,55,0.08)" : "transparent",
                  }}
                  title={e.path}
                >
                  {e.path}
                </div>
              ))}
              {filteredFiles.length === 0 && (
                <div style={{ padding: 14, color: "rgba(255,255,255,0.55)" }}>
                  No hay archivos que coincidan.
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
              <div style={{ color: "rgba(255,255,255,0.8)", fontFamily: "serif", fontSize: "0.9rem" }}>
                {selectedPath ? selectedPath : "Elegí un archivo"}
                {dirty ? " · sin guardar" : ""}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {canEdit && selectedPath && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing((v) => !v)}
                      style={{ ...glass.glassActionBtn, padding: "8px 12px", display: "flex", gap: 6, alignItems: "center" }}
                      title={editing ? "Vista previa" : "Editar"}
                    >
                      {editing ? <Eye size={14} /> : <Pencil size={14} />}
                      {editing ? "Preview" : "Editar"}
                    </button>
                    <button
                      type="button"
                      onClick={saveFile}
                      disabled={!dirty || saving}
                      style={{
                        ...glass.glassActionBtn,
                        padding: "8px 12px",
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        opacity: !dirty || saving ? 0.45 : 1,
                      }}
                      title="Ctrl/Cmd+S"
                    >
                      <Save size={14} />
                      {saving ? "Guardando…" : "Guardar"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {fileError && <div style={{ color: "#ef4444", marginBottom: 10 }}>{fileError}</div>}
            {saveMsg && <div style={{ color: "#86efac", marginBottom: 10 }}>{saveMsg}</div>}

            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 14,
                padding: editing && canEdit ? 0 : 16,
                minHeight: 520,
                overflow: "auto",
              }}
            >
              {loadingFile && <div style={{ color: "rgba(255,255,255,0.6)", padding: 16 }}>Cargando…</div>}

              {!loadingFile && selectedPath && editing && canEdit && (
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: "100%",
                    minHeight: 520,
                    boxSizing: "border-box",
                    resize: "vertical",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "rgba(255,255,255,0.9)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                    fontSize: 13,
                    lineHeight: 1.55,
                    padding: 16,
                  }}
                />
              )}

              {!loadingFile && selectedPath && (!editing || !canEdit) && fileType === "md" && (
                <div style={{ padding: editing ? 16 : 0 }}>
                  <MarkdownRenderer content={editing ? draft : fileContent} className="gh-markdown" />
                </div>
              )}

              {!loadingFile && selectedPath && (!editing || !canEdit) && fileType !== "md" && (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "rgba(255,255,255,0.85)", fontSize: 12, lineHeight: 1.5 }}>
                  {stripHtml(editing ? draft : fileContent)}
                </pre>
              )}

              {!selectedPath && <div style={{ color: "rgba(255,255,255,0.55)", padding: 16 }}>Elegí un .md para editar y guardar en el repo.</div>}
            </div>
          </div>
        </div>
      </div>
    </HomeShell>
  );
}
